/**
 * RUBYN — Internationalization configuration (data-driven).
 *
 * Localized routing strategy: every route lives under `/[locale]/...`
 * (e.g. `/it/fashion`, `/en/beauty`). This gives crawlers real per-language
 * URLs with correct `<html lang>`, `hreflang` and `og:locale` rendered on the
 * server.
 *
 * Body copy comes from the per-locale message catalogs in `messages/` and is
 * resolved *synchronously at render*, so the SSR/SSG HTML is already localized.
 * A locale without a catalog yet (see `CATALOG_LOCALES`) still works: it falls
 * back to the English catalog and WorldLida translates it in the browser, the
 * way the whole site used to.
 *
 * Adding a language = adding one entry here + a `messages/<code>.json`.
 * Nothing else hardcodes the list.
 */

export interface LocaleDefinition {
  /** URL/code segment, also used as `<html lang>` and `hreflang`. */
  code: string;
  /** Native language label, shown in the language switcher. */
  label: string;
  /** English name (for admin / debugging). */
  english: string;
  /** Text direction. */
  dir: "ltr" | "rtl";
  /** OpenGraph locale (e.g. `it_IT`). */
  ogLocale: string;
  /** Default currency suggested for this locale (overridable by the user). */
  defaultCurrency: string;
  /**
   * WorldLida runtime-translation target code (Google Translate code).
   * Only used for locales without a message catalog, and for dynamic strings
   * (product names, supplier copy) that no catalog can cover.
   */
  lidaCode: string;
}

export const LOCALES: readonly LocaleDefinition[] = [
  { code: "en", label: "English",  english: "English",    dir: "ltr", ogLocale: "en_US", defaultCurrency: "USD", lidaCode: "en" },
  { code: "it", label: "Italiano", english: "Italian",    dir: "ltr", ogLocale: "it_IT", defaultCurrency: "EUR", lidaCode: "it" },
  { code: "ru", label: "Русский",  english: "Russian",    dir: "ltr", ogLocale: "ru_RU", defaultCurrency: "RUB", lidaCode: "ru" },
  { code: "es", label: "Español",  english: "Spanish",    dir: "ltr", ogLocale: "es_ES", defaultCurrency: "EUR", lidaCode: "es" },
  { code: "pt", label: "Português", english: "Portuguese", dir: "ltr", ogLocale: "pt_BR", defaultCurrency: "USD", lidaCode: "pt" },
  { code: "fr", label: "Français", english: "French",     dir: "ltr", ogLocale: "fr_FR", defaultCurrency: "EUR", lidaCode: "fr" },
  { code: "de", label: "Deutsch",  english: "German",     dir: "ltr", ogLocale: "de_DE", defaultCurrency: "EUR", lidaCode: "de" },
  { code: "ko", label: "한국어",    english: "Korean",     dir: "ltr", ogLocale: "ko_KR", defaultCurrency: "USD", lidaCode: "ko" },
  { code: "zh", label: "中文",      english: "Chinese",    dir: "ltr", ogLocale: "zh_CN", defaultCurrency: "USD", lidaCode: "zh-CN" },
  { code: "ja", label: "日本語",    english: "Japanese",   dir: "ltr", ogLocale: "ja_JP", defaultCurrency: "JPY", lidaCode: "ja" },
] as const;

export type LocaleCode = (typeof LOCALES)[number]["code"];

/** The source language the site is authored in. */
export const DEFAULT_LOCALE: LocaleCode = "en";

export const LOCALE_CODES: readonly string[] = LOCALES.map((l) => l.code);

/**
 * Locales that ship a hand-maintained `messages/<code>.json`.
 *
 * These render deterministically: the translation is baked into the server
 * HTML, which is the whole point of the migration off runtime WorldLida.
 * Every other locale in `LOCALES` loads the English catalog and is translated
 * client-side by WorldLida as a fallback — same behaviour as before, but now
 * it's the exception rather than the rule. Move a locale here as soon as its
 * catalog is written; nothing else needs to change.
 */
export const CATALOG_LOCALES: readonly LocaleCode[] = ["en", "it"];

/** Does this locale have a real message catalog, or is it on the Lida fallback? */
export function hasCatalog(code: string): boolean {
  return (CATALOG_LOCALES as readonly string[]).includes(code);
}

/** The catalog a locale actually loads (its own, or English as the fallback). */
export function catalogFor(code: string): LocaleCode {
  return hasCatalog(code) ? (code as LocaleCode) : DEFAULT_LOCALE;
}

const LOCALE_MAP: Record<string, LocaleDefinition> = Object.fromEntries(
  LOCALES.map((l) => [l.code, l]),
);

export function isLocale(value: string): value is LocaleCode {
  return value in LOCALE_MAP;
}

export function getLocale(code: string): LocaleDefinition {
  return LOCALE_MAP[code] ?? LOCALE_MAP[DEFAULT_LOCALE];
}

/** Params for `generateStaticParams` — one entry per supported locale. */
export function localeStaticParams(): { locale: string }[] {
  return LOCALES.map((l) => ({ locale: l.code }));
}

/**
 * Map an arbitrary detected language code (e.g. from geoloc — could be a
 * lidaCode like "zh-CN") to one of our offered locales, falling back to the
 * default when the language isn't offered.
 */
export function whitelistLanguage(code?: string): LocaleCode {
  if (!code) return DEFAULT_LOCALE;
  const c = code.toLowerCase();
  const match = LOCALES.find(
    (l) => l.code.toLowerCase() === c || l.lidaCode.toLowerCase() === c,
  );
  return match ? match.code : DEFAULT_LOCALE;
}
